import {
  People as PeopleIcon,
  Star as StarIcon,
  Timeline as TimelineIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  LinearProgress,
  Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { api } from '../api/client';

const LEAD_STATUSES = {
  new: { label: 'Новый', color: '#2196F3', icon: '🆕' },
  contacted: { label: 'В контакте', color: '#FF9800', icon: '📞' },
  qualified: { label: 'Квалифицирован', color: '#4CAF50', icon: '✅' },
  converted: { label: 'Конвертирован', color: '#9C27B0', icon: '🎯' },
  rejected: { label: 'Отклонен', color: '#F44336', icon: '❌' }
};

const LEAD_SOURCES = {
  website: { label: 'Сайт', icon: '🌐' },
  ads: { label: 'Реклама', icon: '📢' },
  referral: { label: 'Рекомендация', icon: '👥' },
  cold_call: { label: 'Холодный звонок', icon: '📞' },
  event: { label: 'Мероприятие', icon: '🎪' },
  social: { label: 'Соц.сети', icon: '📱' },
  other: { label: 'Другое', icon: '❓' }
};

const StatCard = ({ title, value, subtitle, icon, color = 'primary', progress = null }) => (
  <Card
    sx={{
      background: 'rgba(255, 255, 255, 0.1)',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      height: '100%'
    }}
  >
    <CardContent>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)' }} variant="body2" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h4" color={color}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mt: 0.5 }}>
              {subtitle}
            </Typography>
          )}
          {progress !== null && (
            <Box sx={{ mt: 1 }}>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 3
                  }
                }}
              />
            </Box>
          )}
        </Box>
        {icon && (
          <Box sx={{ ml: 2, color: 'action.active' }}>
            {icon}
          </Box>
        )}
      </Box>
    </CardContent>
  </Card>
);

const LeadStats = ({ compact = false }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/leads/stats');
      setStats(response.data);
    } catch (err) {
      setError('Ошибка загрузки статистики');
      console.error('Error loading lead stats:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Card sx={{ p: 3, textAlign: 'center' }}>
        <Typography sx={{ color: 'error.main' }}>{error}</Typography>
      </Card>
    );
  }

  if (!stats) {
    return (
      <Card sx={{ p: 3, textAlign: 'center' }}>
        <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Нет данных для отображения</Typography>
      </Card>
    );
  }

  if (compact) {
    return (
      <Grid container spacing={2}>
        <Grid item xs={6} md={3}>
          <StatCard
            title="Всего лидов"
            value={stats.total || 0}
            icon={<PeopleIcon />}
            color="primary"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            title="Конверсия"
            value={`${stats.conversion_rate || 0}%`}
            icon={<TrendingUpIcon />}
            color="success.main"
            progress={stats.conversion_rate || 0}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            title="Средний рейтинг"
            value={stats.avg_score || 0}
            icon={<StarIcon />}
            color="warning.main"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            title="Конвертировано"
            value={stats.converted_count || 0}
            icon={<TimelineIcon />}
            color="secondary.main"
          />
        </Grid>
      </Grid>
    );
  }

  return (
    <Box>
      {/* Основные метрики */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Всего лидов"
            value={stats.total || 0}
            subtitle="За все время"
            icon={<PeopleIcon />}
            color="primary.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Коэффициент конверсии"
            value={`${stats.conversion_rate || 0}%`}
            subtitle="Лиды в клиенты"
            icon={<TrendingUpIcon />}
            color="success.main"
            progress={stats.conversion_rate || 0}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Средний рейтинг"
            value={`${stats.avg_score || 0}/100`}
            subtitle="Качество лидов"
            icon={<StarIcon />}
            color="warning.main"
            progress={stats.avg_score || 0}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Конвертировано"
            value={stats.converted_count || 0}
            subtitle="Успешных продаж"
            icon={<TimelineIcon />}
            color="secondary.main"
          />
        </Grid>
      </Grid>

      {/* Распределение по статусам */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              p: 2
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
              Лиды по статусам
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {Object.entries(LEAD_STATUSES).map(([status, config]) => {
                const count = stats.by_status?.[status] || 0;
                const percentage = stats.total > 0 ? ((count / stats.total) * 100).toFixed(1) : 0;

                return (
                  <Box key={status}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span>{config.icon}</span>
                        <Typography variant="body2" sx={{ color: 'white' }}>
                          {config.label}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'white' }}>
                        {count} ({percentage}%)
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={parseFloat(percentage)}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: config.color,
                          borderRadius: 4
                        }
                      }}
                    />
                  </Box>
                );
              })}
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card
            sx={{
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              p: 2
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
              Источники лидов
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {Object.entries(stats.by_source || {}).map(([source, count]) => {
                const sourceConfig = LEAD_SOURCES[source] || { label: source, icon: '❓' };
                const percentage = stats.total > 0 ? ((count / stats.total) * 100).toFixed(1) : 0;

                return (
                  <Box key={source} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>{sourceConfig.icon}</span>
                      <Typography variant="body2" sx={{ color: 'white' }}>
                        {sourceConfig.label}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'white' }}>
                        {count}
                      </Typography>
                      <Chip
                        label={`${percentage}%`}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.7rem', height: 20 }}
                      />
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {(!stats.by_source || Object.keys(stats.by_source).length === 0) && (
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', textAlign: 'center', py: 2 }}>
                Нет данных об источниках
              </Typography>
            )}
          </Card>
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* Дополнительная информация */}
      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
          Данные обновляются в реальном времени
        </Typography>
      </Box>
    </Box>
  );
};

export default LeadStats;